import { FC, useEffect, useRef, useState } from "react";
import { OtpModalProps } from "./types";
import {
  Button,
  Card,
  Group,
  Loader,
  Modal,
  PinInput,
  Space,
  Text,
  rem,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import Countdown from "react-countdown";

const OtpModal: FC<OtpModalProps> = ({
  phoneNumber = "0123456789",
  isLoading = false,
  expireTime = 60,
  otp = "",
  onChangeOtp,
  onConfirm,
  handleSendOtp,
  errorMessage,
  modalTitle,
  modalDescription,
  authType,
  beforeInput,
  countdownLabel,
  showCountdownInSeconds = false,
  confirmLabel,
  hideResend = false,
  variant = "default",
  ...modalProps
}) => {
  const isApprovalVariant = variant === "approval";
  const pinWrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const theme = useMantineTheme();
  // Sau mỗi lần gửi lại thành công, khóa nút trong N giây để tránh spam
  const RESEND_COOLDOWN_SECONDS = 30;
  // Rẽ nội dung mô tả mặc định theo phương thức xác thực đang chọn: "2"/"3" = Smart OTP, còn lại = SMS OTP.
  const isSmartOtp = ["2", "3"].includes(String(authType ?? "").trim());

  const [timer, setTimer] = useState(Date.now() + expireTime * 1000);
  const [isExpired, setIsExpired] = useState(false);
  const [countdownKey, setCountdownKey] = useState(0);
  // Chống spam gửi lại OTP: bấm liên tục chỉ gửi 1 request (chặn khi đang bay) + cooldown sau mỗi lần gửi
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // useState là async nên 2 click trong cùng frame đều thấy isResending=false → dùng ref chặn synchronous
  const isResendingRef = useRef(false);
  const canResend =
    !isResending && resendCooldown === 0 && Boolean(handleSendOtp);

  // Nhãn link gửi lại OTP theo trạng thái — tách biến để tránh nested ternary (eslint no-nested-ternary)
  // Bố cục "approval" dùng đúng chữ trong mockup ("Gửi lại mã"); các màn cũ giữ nguyên i18n.
  const resendBaseLabel = isApprovalVariant ? t("RESEND_CODE") : t("RESEND_OTP");
  let resendLabel: string = resendBaseLabel;
  if (isResending) {
    resendLabel = t("RESENDING");
  } else if (resendCooldown > 0) {
    resendLabel = `${resendBaseLabel} (${resendCooldown}s)`;
  }
  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleResend = async () => {
    if (!canResend || !handleSendOtp) return;
    // Guard synchronous: chặn click thứ 2 lọt vào trước khi React kịp re-render isResending
    if (isResendingRef.current) return;
    isResendingRef.current = true;
    setIsResending(true);
    try {
      await handleSendOtp();
      // Gửi thành công: xóa OTP cũ đã nhập, reset đồng hồ hết hạn, bắt đầu cooldown chống spam
      onChangeOtp?.("");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setTimer(Date.now() + expireTime * 1000);
      setIsExpired(false);
      setCountdownKey((k) => k + 1);
    } catch {
      // error handled by caller — không cooldown để người dùng bấm lại được ngay
    } finally {
      isResendingRef.current = false;
      setIsResending(false);
    }
  };
  const focusFirstOtp = () => {
    const firstInput =
      pinWrapperRef.current?.querySelector("input");
    firstInput?.focus();
  };

  // Đếm ngược cooldown gửi lại (mỗi giây trừ 1 cho tới 0)
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timeoutId = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timeoutId);
  }, [resendCooldown]);

  useEffect(() => {
    if (modalProps.opened) {
      setTimer(Date.now() + expireTime * 1000);
      setIsExpired(false);
      setCountdownKey((k) => k + 1);
      setResendCooldown(0);
    }
  }, [modalProps.opened]);
  useEffect(() => {
    if (otp === "" && modalProps.opened) {
      setTimeout(() => {
        focusFirstOtp();
      }, 50);
    }
  }, [otp, modalProps.opened]);

  // Khai một lần, dùng lại ở cả 2 thứ tự bên dưới — tránh lặp chuỗi cảnh báo ở 2 nhánh.
  const lockWarning = (
    <Text fz="xs" c={theme.colors.dark[3]}>
      {t("ACCOUNT_LOCKED_IF_WRONG")}{" "}
      <Text span fw={700} c="#1A95E8">
        {t("FIVE_TIMES")}
      </Text>
    </Text>
  );

  return (
    <Modal zIndex={300} {...modalProps} centered padding={0} styles={{ close: { width: 32, height: 32, svg: { width: 20, height: 20 } } }}>
      <Card radius="md" pb={rem(30)} pt={0}>
        <Card.Section inheritPadding py="xs" pt={0}>
          <Group
            position="left"
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "10px",
              textAlign: "left",
              alignItems: "flex-start",
            }}
          >
            <Text
              style={{ textAlign: "left" }}
              fw={700}
              fz="lg"
            >
              {modalTitle ?? t("CONFIRM_TRANSACTION")}
            </Text>
            <Text fz="sm" w="90%" color={theme.colors.dark[3]}>
              {/* Mô tả mặc định rẽ theo authType: Smart OTP -> hướng dẫn app Javis OTP;
                  còn lại (SMS/không set) -> hướng dẫn SMS OTP. modalDescription (nếu truyền) luôn ưu tiên. */}
              {modalDescription ??
                (isSmartOtp ? (
                  <>
                    {t("PLEASE_ENTER_THE_CODE")}{" "}
                    <Text span fw={700} c="#111827">
                      Smart OTP
                    </Text>{" "}
                    {t("SHOWN_IN_JAVIS_OTP_APP")}
                  </>
                ) : (
                  <>
                    {t("ENTER_OTP_SENT_TO_PHONE")}{" "}
                    <Text span fw={700} c="#111827">
                      {phoneNumber}
                    </Text>{" "}
                    {t("TO_COMPLETE_CONTRACT_SIGNING")}
                  </>
                ))}
            </Text>
            {beforeInput}
            <div ref={pinWrapperRef}>
              <PinInput
                oneTimeCode
                size="md"
                type="number"
                length={6}
                value={otp}
                onChange={onChangeOtp}
                disabled={isExpired}
                styles={{
                  root: {
                    gap: 20,
                    justifyContent: "flex-start",
                  },
                  input: {
                    width: 40,
                    height: 60,
                    borderRadius: 10,
                    background: "#EFF6FF",
                    border: "1px solid transparent",
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 16,
                    color: "#000000",

                    "&:not(:placeholder-shown)": {
                      color: "#000000",
                    },
                    "::placeholder": {
                      color: "#eff6ff",
                      opacity: 1,
                      fontWeight: 500,
                    },
                    paddingBottom: 6,
                    boxShadow: "none",
                    backgroundImage: "linear-gradient(#2A81D0, #2A81D0)",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center calc(100% - 8px)",
                    backgroundSize: "12px 3px",

                    "&:focus": {
                      borderColor: "#2A81D0",
                      outline: "none",
                    },

                    "&:disabled": {
                      background: "#F1F3F5",
                      backgroundImage: "linear-gradient(#ADB5BD, #ADB5BD)",
                      color: "#ADB5BD",
                      cursor: "not-allowed",
                      opacity: 0.7,
                    },
                  },
                }}
              />
            </div>
            {errorMessage && (
              <Text fz="xs" c="#EF4444" fw={500}>
                {errorMessage}
              </Text>
            )}
            {/* Hai khối dưới ô nhập mã đổi CHỖ theo variant, nội dung giữ nguyên:
                - "default": đếm ngược + gửi lại nằm cùng dòng, cảnh báo khoá ở dưới (như cũ)
                - "approval": cảnh báo khoá lên trước, đếm ngược trái ↔ gửi lại phải (mockup) */}
            {isApprovalVariant && lockWarning}
            <Group
              position={isApprovalVariant ? "apart" : "left"}
              spacing={8}
              w={isApprovalVariant ? "100%" : undefined}
              noWrap={isApprovalVariant}
            >
              {/* spacing 8 ở nhánh "default" để khoảng cách nhãn ↔ đồng hồ y hệt trước khi tách Group */}
              <Group spacing={isApprovalVariant ? 6 : 8} noWrap>
                <Text fz="xs" color={theme.colors.dark[3]}>
                  {countdownLabel ?? t("OTP_EXPIRES_IN")}
                </Text>
                <Countdown
                  key={countdownKey}
                  date={timer}
                  onComplete={() => {
                    setIsExpired(true);
                    onChangeOtp?.("");
                    notifications.show({
                      title: t("NOTICE"),
                      message:
                        t("OTP_VERIFICATION_TIMED_OUT"),
                      color: "orange",
                    });
                  }}
                  renderer={({ minutes, seconds }) => (
                    <Text
                      fz="xs"
                      fw={isApprovalVariant ? 700 : 400}
                      color={isApprovalVariant ? "#0F172A" : "#99A0AE"}
                    >
                      {showCountdownInSeconds
                        ? `${minutes * 60 + seconds}s`
                        : `${minutes.toString().padStart(2, "0")}:${seconds
                            .toString()
                            .padStart(2, "0")}`}
                    </Text>
                  )}
                />
              </Group>

              {/* Smart OTP không có "gửi lại" (mã sinh trên app KeyPass) -> ẩn hẳn link
                  khi hideResend=true; mặc định giữ nguyên link như cũ (SMS OTP). */}
              {!hideResend && (
                <Text
                  style={{ cursor: canResend ? "pointer" : "not-allowed" }}
                  color={canResend ? "#1A95E8" : "#99A0AE"}
                  fz="xs"
                  fw={600}
                  onClick={canResend ? handleResend : undefined}
                >
                  {resendLabel}
                </Text>
              )}
            </Group>
            {!isApprovalVariant && lockWarning}
            <Space h={20} />
          </Group>
          <Group position="center">
            <Button
              color="primary"
              onClick={onConfirm}
              disabled={isLoading || isExpired || !otp || otp.length < 6}
              style={{ width: "100%", borderRadius: "8px" }}
            >
              {isLoading ? (
                <Loader size="xs" color="white" />
              ) : (
                confirmLabel ?? t("NEXT")
              )}
            </Button>
          </Group>
        </Card.Section>
      </Card>
    </Modal>
  );
};

export default OtpModal;
